import { useEffect } from "react";
import { Link } from "react-router-dom";
import { TextPageLayout } from "@/components/TextPageLayout";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags } from "@/lib/seo";
import { generateOrganizationSchema, generateWebPageSchema, addSchemaToPage } from "@/lib/schema";

export default function AboutPage() {
  useEffect(() => {
    const title = "About - Munthly";
    const description = "Learn about Munthly, a free budgeting tool that helps you take control of your finances. Discover the Munthly Method and how it works.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/about");
    const ogCleanup = setOpenGraphTags(title, description, "/about");
    const twitterCleanup = setTwitterCardTags(title, description);
    
    // Add schema for about page
    const organizationSchema = generateOrganizationSchema();
    const webPageSchema = generateWebPageSchema(
      "/about",
      title,
      description
    );
    
    const schemaCleanup = addSchemaToPage([organizationSchema, webPageSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      schemaCleanup();
    };
  }, []);

  return (
    <TextPageLayout
      title="About Munthly"
      subtitle="A free budgeting tool that helps you take control of your finances"
    >
      <div className="space-y-6 text-base leading-relaxed">
        <p className="text-lg">
          Munthly is a free budgeting tool designed to help you get control of your finances quickly, easily, and effectively. No subscriptions, no hidden fees, no complicated setup — just a straightforward way to understand and manage your money.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Our Mission</h2>
        <p>
          We believe that everyone deserves access to tools that help them manage their finances better. That's why we've built Munthly as a completely free budgeting tool. Our mission is simple: to offer a free, accessible way for people to get control of their finances without barriers or complexity.
        </p>
        <p>
          Financial planning shouldn't be complicated or expensive. Whether you're trying to save for a holiday, manage your monthly bills, or build an emergency fund, Munthly gives you the tools you need—at no cost.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">The Munthly Method</h2>
        <p>
          At the heart of Munthly is the Munthly Method — a simple approach to budgeting that turns all your income and expenses into a stable monthly balance sheet. Instead of letting your spending yo-yo from month to month, the Munthly Method helps you create a predictable financial picture.
        </p>
        <p>
          By splitting future costs across the months leading up to them, you only spend money you actually have available. No surprises, no scrambling. Just clarity and control.
        </p>
        <p>
          <Link to="/method" className="text-primary hover:underline font-medium">
            Learn more about the Munthly Method →
          </Link>
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">What Makes Munthly Different</h2>
        <p>
          Most budgeting tools are either too complicated or too expensive. Munthly is different:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li><strong className="text-foreground">Completely free</strong> — no subscriptions, no premium tiers, no hidden costs</li>
          <li><strong className="text-foreground">Simple and quick</strong> — get started in minutes, not hours</li>
          <li><strong className="text-foreground">Effective</strong> — the Munthly Method creates a stable, predictable balance sheet</li>
          <li><strong className="text-foreground">Private and secure</strong> — your data stays yours, stored securely and never shared</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">How It Works</h2>
        <p>
          Munthly helps you organise your finances into three clear categories: regular expenses (your monthly bills), provisions (future one-off costs), and savings (ongoing funds or goals). Add your income, log your expenses, and Munthly automatically breaks everything down into monthly amounts.
        </p>
        <p>
          The result? A clear view of your finances that doesn't change dramatically from month to month. You'll know exactly where you stand, and you'll be prepared for what's coming.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Free Budgeting Tool for Everyone</h2>
        <p>
          Munthly is and always will be a free budgeting tool. We're committed to providing valuable financial management tools without any cost barriers, because we believe good financial planning should be accessible to everyone — regardless of income or financial situation.
        </p>
        <p>
          Whether you're just starting to budget or looking for a better way to manage your money, Munthly is here to help. No credit card required, no trial periods, no catches. Just a free budgeting tool that works.
        </p>

        <div className="bg-[var(--surface-container)] rounded-2xl p-6 mt-8">
          <h3 className="font-semibold mb-2">Ready to get started?</h3>
          <p className="text-sm text-muted-foreground">
            <Link to="/signup" className="text-primary hover:underline font-medium">Sign up for free</Link> and start taking control of your finances today. It takes just a few minutes to set up, and you'll immediately see how the Munthly Method can help you create a more stable financial future.
          </p>
        </div>
      </div>
    </TextPageLayout>
  );
}
